import { initialStateByModule } from './state'
import { map, omit, zipObject } from 'lodash'
import moment from 'moment'
import { convertStringToNumber } from '@/utils/filters'
import {
  createPlanApi,
  getPaymentMethodsOptions,
  getCardBrand,
  postInstallments
} from '@/api/services'

const CHANGE_BRAND = 'changeBrand'
const CHANGE_TYPE_PAYMENT = 'changeTypePayment'
const CHANGE_NUMBER_CARD = 'changeNumberCard'
const CHANGE_NAME_CARD = 'changeNameCard'
const CHANGE_VALIDATE_CARD = 'changeValidateCard'
const CHANGE_SECURITY_CODE = 'changeSecurityCode'
const CHANGE_CPF_CARD = 'changeCpfCard'
const CHANGE_PARCELS = 'changeParcels'
const CHANGE_ACTIVE_VOUCHER = 'changeActiveVoucher'
const CHANGE_PAYMENT_METHOD_DATA = 'changePaymentMethodData'

const CHANGE_CAMPAIGN = 'changeCampaign'

const GET_PAYMENT_PARCELS = 'getPaymentParcels'

const SET_TOKEN = 'setToken'
const SET_METHODS_OPTIONS = 'setMethodsOptions'
const CHANGE_INSTALLMENTS = 'changeInstallments'
const CHANGE_ASYNC_PENDING = 'changeAsyncPending'

const CREATE_PLAN = 'createPlan'
const PLAN_PENDING = 'planPending'
const PLAN_SUCCESS = 'planSuccess'
const PLAN_FAILURE = 'planFailure'
const CHANGE_PLAN_UUID = 'changePlanUuid'
const CHANGE_ACCEPTED_CONDITIONS = 'changeAcceptedConditions'
const IS_PROMO_FIRST_INSTALLMENT = 'isPromoFirstInstallment'

const state = initialStateByModule('payment')

const getters = {
  isAnnual (state, getters, rootState) {
    return rootState.plan.annualValue && /BANK_SLIP|PIX/.test(state.typePayment.method)
  },
  expirationYear (state, getters, rootState) {
    return state.validateCard.toString().substr(2, 2)
  },
  expirationMonth (state) {
    return state.validateCard.toString().substr(0, 2)
  },
  bin (state) {
    return state.numberCard.toString().substr(0, 6)
  },
  purchaseContract (state, getters, rootState, rootGetters) {
    const _rootStateAddress = {
      zipCode: rootState.address.zipcode,
      publicPlace: rootState.address.streetName,
      numberAddress: rootState.address.streetNumber,
      additionalAddressInformation: rootState.address.complement,
      neighborhood: rootState.address.neighborhood,
      city: rootState.address.city,
      state: rootState.address.state
    }

    const paymentData = {
      planUuid: rootState.product.plan.uuid,
      leadUuid: '',
      ...(!rootState.personalData.isEmployee && {
        payment: {
          amount: convertStringToNumber(rootState.plan.precoTotal),
          cpf: rootState.personalData.cpf,
          ...(Object.hasOwn(state.typePayment, 'isInstant') && {
            isInstant: state.typePayment.isInstant
          })
        }
      }),
      promoCode: '',
      listPersons: [
        {
          name: rootState.personalData.name,
          birthDate: moment(rootState.personalData.birthday, 'DDMMYYYY').format('YYYY-MM-DD'),
          gender: rootState.personalData.gender,
          cpf: rootState.personalData.cpf,
          phone: {
            phone: rootState.personalData.cellphone
          },
          email: {
            email: rootState.personalData.email.toLowerCase(),
            checkEmail: rootState.personalData.email.toLowerCase()
          },
          personType: 'SUCESSOR_HOLDER',
          relationDegree: 'HOLDER',
          address: _rootStateAddress
        }
      ],
      authorize: true,
      ...(Object.hasOwn(rootState.product, 'operator') && Object.hasOwn(rootState.product, 'store') && {
        operator: rootState.product.operator,
        store: rootState.product.store
      }),
      ...(rootState.plan?.campaign?.active && {
        promo: { ...rootState.plan.campaign }
      })
    }

    if (!rootState.personalData.isEmployee) {
      if (rootState.product.payment?.length > 0) { // product has payment config to apply
        map(rootState.product.payment, item => {
          map(item.items, node => {
            // grab key/value pairs from config entry, except byRef attribute, used for determine config value type
            const keys = node.byRef
              ? Object.keys(omit(node, ['byRef']))
              : Object.keys(node)

            let values = node.byRef
              ? Object.values(omit(node, ['byRef']))
              : Object.values(node)

            if (node.byRef) { // config value is by reference, so, get it
              values = map(values, value => rootGetters[value])
            }

            // inject config into paymentData object specific node
            Object.assign(item.section === 'root'
              ? paymentData
              : paymentData[item.section], zipObject(keys, values))
          })
        })
      }

      if (state.typePayment.method === 'VOUCHER') {
        paymentData.payment.voucher = {
          ...rootState.payment.paymentMethodData
        }
      } else if (state.typePayment.method === 'SCHEDULED_DEBIT') {
        paymentData.payment.scheduledDebit = {
          ...rootState.payment.paymentMethodData
        }
      } else if (state.typePayment.method === 'BANK_SLIP') {
        paymentData.payment.amount = convertStringToNumber(
          rootState.plan.annualValue || rootState.plan.precoTotal
        )

        if (rootState.product.campaign) {
          paymentData.payment.campaign = rootState.product.campaign
        }

        paymentData.payment.bankSlip = {
          ...rootState.payment.paymentMethodData
        }
      } else if (state.typePayment.method === 'PIX') {
        paymentData.payment.amount = convertStringToNumber(
          rootState.plan.annualValue || rootState.plan.precoTotal
        )

        if (rootState.product.campaign) {
          paymentData.payment.campaign = rootState.product.campaign
        }

        paymentData.payment.pix = {
          cpf: rootState.personalData.cpf,
          email: rootState.personalData.email.toLowerCase(),
          name: rootState.personalData.name
        }
      } else if (state.typePayment.method === 'MERCADO_PAGO') {
        paymentData.payment.mercadoPago = {
          name: rootState.personalData.name,
          cpf: rootState.personalData.cpf,
          email: rootState.personalData.email.toLowerCase(),
          backUrls: {
            success: `${window.location.origin}${rootState.product.redirect}`,
            pending: `${window.location.origin}${rootState.product.redirect}`,
            failure: `${window.location.origin}${rootState.product.redirect}`
          }
        }
      } else {
        paymentData.payment.cpf = rootState.payment.cpfCard

        const getAttrName = state.typePayment.method === 'CREDIT_CARD' ? 'creditCard' : 'card'
        paymentData.payment[getAttrName] = {
          brand: state.brand.toUpperCase(),
          cardNumber: state.numberCard,
          expirationDate: `${getters.expirationMonth}/20${getters.expirationYear}`,
          holder: state.nameCard.toUpperCase(),
          ...(state.securityCode && { securityCode: state.securityCode })
        }
      }
    }

    return {
      apiPath: rootState.product.api,
      headersData: {
        'recaptcha-token': state.token,
        distribuition_partner: rootState.product.distribuitionPartner
      },
      paymentData
    }
  },
  [GET_PAYMENT_PARCELS]: state => state.parcels
}

const mutations = {
  [SET_TOKEN] (state, payload) {
    state.token = payload
  },
  [SET_METHODS_OPTIONS] (state, payload) {
    state.methodsOptions = payload
  },
  [CHANGE_INSTALLMENTS] (state, { paymentMethod, installments }) {
    state.installments[paymentMethod] = installments
  },
  [PLAN_PENDING] (state) {
    state.asyncPending = true
  },
  [PLAN_SUCCESS] (state, payload) {
    state.planUuid = payload.data.planUuid || payload.data?.plan?.uuid
    state.asyncPending = false
  },
  [PLAN_FAILURE] (state) {
    state.asyncPending = false
  },
  [CHANGE_PLAN_UUID] (state, payload) {
    state.planUuid = payload
  },
  [CHANGE_ASYNC_PENDING] (state, payload) {
    state.asyncPending = payload
  },
  [CHANGE_BRAND] (state, payload) {
    state.brand = payload
  },
  [CHANGE_TYPE_PAYMENT] (state, payload) {
    state.typePayment = payload
  },
  [CHANGE_NUMBER_CARD] (state, payload) {
    state.numberCard = payload
    state.lastCardNumbers = payload.replace(/\d{12}(\d{4})/g, '$1')
    if (state.numberCard.toString().length < 6) {
      state.brand = ''
    }
  },
  [CHANGE_NAME_CARD] (state, payload) {
    state.nameCard = payload
  },
  [CHANGE_VALIDATE_CARD] (state, payload) {
    state.validateCard = payload
  },
  [CHANGE_SECURITY_CODE] (state, payload) {
    state.securityCode = payload
  },
  [CHANGE_CPF_CARD] (state, payload) {
    state.cpfCard = payload
  },
  [CHANGE_PARCELS] (state, payload) {
    state.parcels = payload
  },
  [CHANGE_CAMPAIGN] (state, payload) {
    state.campaign = payload
  },
  [CHANGE_ACCEPTED_CONDITIONS] (state, payload) {
    state.acceptedConditions = payload
  },
  [CHANGE_ACTIVE_VOUCHER] (state, payload) {
    state.isActiveVoucher = payload
  },
  [CHANGE_PAYMENT_METHOD_DATA] (state, payload) {
    state.paymentMethodData = payload
  },
  [IS_PROMO_FIRST_INSTALLMENT] (state, payload) {
    state.isPromoFirstInstallment = payload
  }
}

const actions = {
  getPaymentMethodsOptions ({ commit, rootState }) {
    const payload = {}

    if (rootState.product.distribuitionPartner) {
      payload.headers = {
        partner: rootState.product.distribuitionPartner
      }
    }

    payload.data = {
      product: rootState.product?.alias?.replace('-', '_')
    }

    return getPaymentMethodsOptions(payload)
      .then(res => {
        if (!res.data || res.data.length === 0) {
          commit(SET_METHODS_OPTIONS, [{
            method: 'CREDIT_CARD',
            installments: 1
          }])
          return
        }

        commit(IS_PROMO_FIRST_INSTALLMENT, Boolean(res.data.find(item => item.hasPromoFirstInstallment === true)))
        commit(SET_METHODS_OPTIONS, res.data)
      })
      .catch(error => {
        this._vm.$sentry.logWithContext({
          level: 'error',
          tags: {
            app_product: rootState.product.alias,
            app_step: rootState.steps.currentStep,
            app_errorZone: 'getPaymentMethodsOptions'
          },
          ctx: {
            label: '[Payment] - getPaymentMethodsOptions'
          },
          error
        })
      })
  },
  async getTokenReCAPTCHA ({ commit, rootState }) {
    try {
      await window.grecaptcha.execute(process.env.VUE_APP_RECAPTCHA, {
        action: 'ecommerce'
      })
        .then(function (token) {
          commit(SET_TOKEN, token)
        })
    } catch (error) {
      this._vm.$sentry.logWithContext({
        level: 'error',
        tags: {
          app_product: rootState.product.alias,
          app_step: rootState.steps.currentStep,
          app_errorZone: 'reCAPTCHA'
        },
        ctx: {
          label: '[Payment] - reCAPTCHA'
        },
        error
      })
    }
  },
  getCardBrand ({ getters, commit, dispatch, rootState }) {
    const bin = getters.bin
    commit(CHANGE_ASYNC_PENDING, true)

    return getCardBrand({
      bin,
      data: {
        product: rootState.product.aliasEN,
        partner: rootState.product.distribuitionPartner,
        ...(rootState.product.campaign && { campaign: rootState.product.campaign }),
        ...(rootState.payment.typePayment.method === 'VIRTUAL_DEBIT_CAIXA' && {
          isVirtualDebitCaixa: true
        })
      }
    })
      .then(res => {
        /*
         * Verificar se a propriedade 'status' é diferente de 00
         * 01 - Bandeira não suportada
         * 02 - Cartão não suportado na consulta de bin
         * 73 - Afiliação bloqueada
        */
        if (res.data.code !== '00') {
          commit(CHANGE_ASYNC_PENDING, false)
          return
        }

        commit(CHANGE_BRAND, res.data.provider)

        commit(CHANGE_ASYNC_PENDING, false)
      })
      .catch(error => {
        this._vm.$sentry.logWithContext({
          level: 'error',
          tags: {
            app_product: rootState.product.alias,
            app_step: rootState.steps.currentStep,
            app_errorZone: 'getCardBrand'
          },
          ctx: {
            label: '[Payment] - getCardBrand'
          },
          error
        })

        commit(CHANGE_BRAND, '')
        commit(CHANGE_ASYNC_PENDING, false)

        rootState.payment.campaign.hasCampaign && dispatch('cleanCampaign')
      })
  },
  getInstallments ({ commit, rootState }, payload) {
    rootState.payment.parcels = ''

    const { installments, typePayment } = rootState.payment

    if (
      !typePayment.method ||
      (installments[typePayment.method] && installments[typePayment.method].length > 0)
    ) return

    return postInstallments({
      amount: convertStringToNumber(rootState.plan.precoTotal),
      paymentMethod: typePayment.method,
      product: rootState.product.alias && rootState.product.alias.replace('-', '_'),
      isRecurrent: rootState.product.recurrent,
      ...payload && payload.nonce && { nonce: payload.nonce }
    })
      .then(res => {
        try {
          const installments = res.data.map((installment, i) => {
            return {
              label: installment.label,
              value: installment.value.term || installment.value,
              ...installment.value.monthly_payment && {
                monthly_payment: installment.value.monthly_payment
              }
            }
          })

          commit(CHANGE_INSTALLMENTS, {
            paymentMethod: typePayment.method,
            installments
          })

          commit(
            'plan/changeValueInstallments',
            res.data.map(installment => installment.amount),
            { root: true }
          )
        } catch (error) {
          this._vm.$sentry.logWithContext({
            level: 'error',
            tags: {
              app_product: rootState.product.alias,
              app_step: rootState.steps.currentStep,
              app_errorZone: 'getInstallments'
            },
            ctx: {
              label: '[Payment] - getInstallments'
            },
            error
          })
        }
      })
      .catch(error => {
        this._vm.$sentry.logWithContext({
          level: 'error',
          tags: {
            app_product: rootState.product.alias,
            app_step: rootState.steps.currentStep,
            app_errorZone: 'postInstallments'
          },
          ctx: {
            label: '[Payment] - postInstallments'
          },
          error
        })
      })
  },
  [CREATE_PLAN] ({ getters, commit, rootState }) {
    try {
      commit(PLAN_PENDING)

      return createPlanApi(getters.purchaseContract)
        .then(res => {
          commit(PLAN_SUCCESS, res)
          return res
        })
        .catch(error => {
          commit(PLAN_FAILURE, error)
          throw error
        })
    } catch (error) {
      commit(PLAN_FAILURE)
      this._vm.$sentry.logWithContext({
        level: 'error',
        tags: {
          app_product: rootState.product.alias,
          app_step: rootState.steps.currentStep,
          app_paymentMethod: rootState.payment.typePayment.method,
          app_errorZone: 'createPlan'
        },
        ctx: {
          label: '[Payment] - createPlan'
        },
        error
      })
    }
  },
  [CHANGE_PARCELS] ({ commit, dispatch }, payload) {
    commit(CHANGE_PARCELS, payload)

    dispatch('plan/changePlanParcel', payload, { root: true })
  },
  cleanCampaign ({ commit, dispatch }) {
    commit(CHANGE_CAMPAIGN, { hasCampaign: false })

    dispatch('plan/changePlanPromoCodeCode', {
      code: null,
      search: true
    }, { root: true })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
